/**
 * GL Audit Trail Viewer Component
 * 
 * Displays immutable audit trail for compliance and forensics
 */

import React, { useState, useEffect } from 'react';
import { auditTrailAPI } from '../services/accountingService';
import {
  NotePencil, CheckCircle, ArrowUUpLeft, PaperPlaneTilt, LockKey, Bank,
  Checks, PushPin, MagnifyingGlass, DownloadSimple, ShieldCheck,
} from '@phosphor-icons/react';
import '../styles/auditTrailViewer.css';

const AuditTrailViewer = () => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState('date-range');
  const [filterValue, setFilterValue] = useState({
    startDate: '',
    endDate: '',
    action: '',
    userId: '',
  });
  const [auditSummary, setAuditSummary] = useState(null);

  useEffect(() => {
    fetchAuditSummary();
  }, []);

  const fetchAuditSummary = async () => {
    try {
      setLoading(true);
      const summary = await auditTrailAPI.getAuditSummary();
      setAuditSummary(summary);
    } catch (err) {
      console.error('Error fetching audit summary:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      let result;

      if (filterType === 'date-range') {
        result = await auditTrailAPI.getAuditByDateRange(
          filterValue.startDate,
          filterValue.endDate
        );
      } else if (filterType === 'action') {
        result = await auditTrailAPI.getAuditByAction(filterValue.action);
      } else if (filterType === 'user') {
        result = await auditTrailAPI.getAuditByUser(filterValue.userId);
      }

      setAuditLogs(result || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const result = await auditTrailAPI.exportAuditTrail(
        filterValue.entityType || 'JOURNAL_ENTRY',
        filterValue.entityId || 'all'
      );
      // Trigger download
      console.log('Export initiated:', result);
    } catch (err) {
      setError(err.message);
    }
  };

  const getActionIcon = (action) => {
    const icons = {
      'ACCOUNT_CREATED': NotePencil,
      'ENTRY_POSTED': CheckCircle,
      'ENTRY_REVERSED': ArrowUUpLeft,
      'EXPENSE_APPROVED': CheckCircle,
      'EXPENSE_POSTED': PaperPlaneTilt,
      'PERIOD_LOCKED': LockKey,
      'PERIOD_CLOSED': CheckCircle,
      'BANK_MATCHED': Bank,
      'RECONCILIATION_COMPLETED': Checks,
    };
    const Icon = icons[action] || PushPin;
    return <Icon size={16} />;
  };

  if (loading) return <div className="loading">Loading audit trail...</div>;

  return (
    <div className="audit-trail-viewer">
      <div className="header">
        <h2><MagnifyingGlass size={20} /> GL Audit Trail</h2>
        <p>Immutable record of all accounting operations for compliance and forensics</p>
      </div>

      {error && <div className="error-message">{error}</div>}

      {auditSummary && (
        <div className="audit-summary">
          <div className="summary-card">
            <h4>Total Actions</h4>
            <p className="stat">{auditSummary.total_actions || 0}</p>
          </div>
          <div className="summary-card">
            <h4>Last 24 Hours</h4>
            <p className="stat">{auditSummary.last_24_hours || 0}</p>
          </div>
          <div className="summary-card">
            <h4>Active Users</h4>
            <p className="stat">{auditSummary.active_users || 0}</p>
          </div>
          <div className="summary-card">
            <h4>Reversals</h4>
            <p className="stat">{auditSummary.total_reversals || 0}</p>
          </div>
        </div>
      )}

      <form onSubmit={handleFilter} className="filter-form">
        <h3>Filter Audit Trail</h3>
        <div className="filter-options">
          <div className="option">
            <input
              type="radio"
              name="filterType"
              value="date-range"
              checked={filterType === 'date-range'}
              onChange={(e) => setFilterType(e.target.value)}
            />
            <label>By Date Range</label>
          </div>
          <div className="option">
            <input
              type="radio"
              name="filterType"
              value="action"
              checked={filterType === 'action'}
              onChange={(e) => setFilterType(e.target.value)}
            />
            <label>By Action</label>
          </div>
          <div className="option">
            <input
              type="radio"
              name="filterType"
              value="user"
              checked={filterType === 'user'}
              onChange={(e) => setFilterType(e.target.value)}
            />
            <label>By User</label>
          </div>
        </div>

        {filterType === 'date-range' && (
          <div className="filter-inputs">
            <input
              type="date"
              value={filterValue.startDate}
              onChange={(e) => setFilterValue({...filterValue, startDate: e.target.value})}
              placeholder="Start Date"
            />
            <input
              type="date"
              value={filterValue.endDate}
              onChange={(e) => setFilterValue({...filterValue, endDate: e.target.value})}
              placeholder="End Date"
            />
          </div>
        )}

        {filterType === 'action' && (
          <input
            type="text"
            value={filterValue.action}
            onChange={(e) => setFilterValue({...filterValue, action: e.target.value})}
            placeholder="e.g., ENTRY_POSTED, EXPENSE_APPROVED"
          />
        )}

        {filterType === 'user' && (
          <input
            type="text"
            value={filterValue.userId}
            onChange={(e) => setFilterValue({...filterValue, userId: e.target.value})}
            placeholder="User ID"
          />
        )}

        <button type="submit" className="btn-primary"><MagnifyingGlass size={16} /> Search</button>
        <button type="button" className="btn-secondary" onClick={handleExport}>
          <DownloadSimple size={16} /> Export
        </button>
      </form>

      <div className="audit-logs-table">
        <h3>Audit Events</h3>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>Action</th>
                <th>Entity Type</th>
                <th>Entity ID</th>
                <th>User</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {auditLogs.length === 0 ? (
                <tr>
                  <td colSpan="6" className="empty">No audit logs found</td>
                </tr>
              ) : (
                auditLogs.map((log, index) => (
                  <tr key={index}>
                    <td className="timestamp">
                      {new Date(log.timestamp).toLocaleString()}
                    </td>
                    <td className="action">
                      <span>{getActionIcon(log.action)} {log.action}</span>
                    </td>
                    <td>{log.entity_type}</td>
                    <td className="entity-id">{log.entity_id?.substring(0, 8)}...</td>
                    <td className="user">
                      {log.user_name}
                      <small>({log.user_role})</small>
                    </td>
                    <td className="details">
                      <button 
                        className="btn-tiny"
                        title={log.change_summary}
                      >
                        ℹ️ View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="compliance-info">
        <h4><ShieldCheck size={18} /> Audit Trail Compliance</h4>
        <ul>
          <li><CheckCircle size={14} /> Immutable record - No deletions allowed</li>
          <li><CheckCircle size={14} /> Complete history - Every GL operation logged</li>
          <li><CheckCircle size={14} /> User tracking - User ID, name, and role recorded</li>
          <li><CheckCircle size={14} /> Before/After values - All changes tracked</li>
          <li><CheckCircle size={14} /> IP tracking - Source IP recorded for security</li>
          <li><CheckCircle size={14} /> Batch tracking - Related operations grouped</li>
        </ul>
      </div>
    </div>
  );
};

export default AuditTrailViewer;
